import React, { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Star } from 'lucide-react';

const CARD_WIDTH = 374; // 350px card + 24px gap

const TestimonialsCarousel = ({ testimonials = [] }) => {
  const trackRef = useRef(null);
  const [paused, setPaused] = useState(false);

  // Duplicate the list to enable a visually-infinite auto-scroll.
  const items = testimonials.length > 0 ? [...testimonials, ...testimonials] : [];

  // Auto-scroll loop : avance d'un petit pas (~ 60 fps) tant que pas en pause.
  // Quand on dépasse la moitié (= longueur originale), on revient à 0 sans saccade.
  useEffect(() => {
    const el = trackRef.current;
    if (!el || testimonials.length === 0) return undefined;

    let raf;
    let last = performance.now();
    const speed = 30; // px par seconde

    const halfWidth = () => testimonials.length * CARD_WIDTH;

    const step = (now) => {
      const dt = (now - last) / 1000;
      last = now;
      if (!paused && el) {
        el.scrollLeft += speed * dt;
        if (el.scrollLeft >= halfWidth()) {
          el.scrollLeft -= halfWidth();
        }
      }
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [paused, testimonials.length]);

  const scrollByCard = (direction) => {
    const el = trackRef.current;
    if (!el) return;
    setPaused(true);
    el.scrollBy({ left: direction * CARD_WIDTH, behavior: 'smooth' });
    // Reprend l'auto-scroll après 4s d'inactivité utilisateur
    window.clearTimeout(scrollByCard._t);
    scrollByCard._t = window.setTimeout(() => setPaused(false), 4000);
  };

  if (testimonials.length === 0) return null;

  return (
    <div
      className="relative"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      data-testid="testimonials-carousel"
    >
      {/* Flèche gauche */}
      <button
        type="button"
        onClick={() => scrollByCard(-1)}
        className="hidden sm:flex absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 z-20 h-11 w-11 items-center justify-center rounded-full bg-white shadow-xl border border-slate-200 text-[#285BBB] hover:bg-[#285BBB] hover:text-white transition-colors"
        aria-label="Avis précédent"
        data-testid="testimonials-prev"
      >
        <ChevronLeft className="h-5 w-5" />
      </button>

      {/* Flèche droite */}
      <button
        type="button"
        onClick={() => scrollByCard(1)}
        className="hidden sm:flex absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 z-20 h-11 w-11 items-center justify-center rounded-full bg-white shadow-xl border border-slate-200 text-[#285BBB] hover:bg-[#285BBB] hover:text-white transition-colors"
        aria-label="Avis suivant"
        data-testid="testimonials-next"
      >
        <ChevronRight className="h-5 w-5" />
      </button>

      <div
        ref={trackRef}
        id="testimonials-track"
        className="flex gap-6 overflow-x-auto pb-3 px-1"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        data-testid="testimonials-track"
      >
        <style>{`#testimonials-track::-webkit-scrollbar{display:none;}`}</style>
        {items.map((review, idx) => (
          <div
            key={idx}
            className="flex-shrink-0 w-[350px] bg-white rounded-2xl p-6 shadow-lg"
            data-testid={`testimonial-card-${idx}`}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-[#285BBB] to-[#2C3C65] rounded-full flex items-center justify-center text-white font-bold text-lg">
                {(review.name || '?').charAt(0)}
              </div>
              <div>
                <p className="font-semibold text-[#2C3C65]">{review.name}</p>
                <p className="text-xs text-gray-400">{review.date}</p>
              </div>
            </div>
            <div className="flex gap-1 mb-3">
              {[...Array(review.rating || 5)].map((_, i) => (
                <Star key={i} className="h-4 w-4 text-yellow-400 fill-yellow-400" />
              ))}
            </div>
            <p className="text-[#6777A1] text-sm leading-relaxed">
              {review.text
                ? <>&laquo;&nbsp;{review.text}&nbsp;&raquo;</>
                : <span className="italic text-[#6777A1]/70">A laissé une note 5 étoiles sans commentaire écrit.</span>
              }
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TestimonialsCarousel;
